import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const HTMLKeyboardNav = () => {
    const naturallyFocusableCode = `<!-- Naturally focusable elements --&gt;
<a href="/page">Link</a>
<button>Button</button>
<input type="text">
<textarea></textarea>
<select></select>
<audio controls></audio>
<video controls></video>

<!-- Users can Tab to these elements --&gt;`;

    const makeFocusableCode = `<!-- Add tabindex="0" to make focusable --&gt;
<div tabindex="0" role="button" onclick="doSomething()">
    Custom Button
</div>

<!-- tabindex values: --&gt;
<!-- 0: Natural tab order --&gt;
<!-- -1: Programmatically focusable (not in tab order) --&gt;
<!-- 1+: Custom tab order (avoid - confusing!) --&gt;`;

    const removeFromTabOrderCode = `<!-- Remove from tab order --&gt;
<button tabindex="-1">Not keyboard accessible</button>

<!-- Useful for: --&gt;
<!-- - Disabled states --&gt;
<!-- - Hidden content --&gt;
<!-- - Programmatic focus only --&gt;`;

    const alwaysShowFocusCode = `<style>
/* Default browser focus (usually blue outline) */
button:focus {
    /* NEVER do this: */
    /* outline: none;  */
}

/* Good: Custom but visible focus */
button:focus {
    outline: 3px solid #4CAF50;
    outline-offset: 2px;
}

/* Modern approach: focus-visible (keyboard only) */
button:focus-visible {
    outline: 3px solid #4CAF50;
    outline-offset: 2px;
}

/* Remove focus for mouse clicks only */
button:focus:not(:focus-visible) {
    outline: none;
}
</style>`;

    const focusStylesCode = `<style>
/* Links */
a:focus {
    outline: 2px solid #2196F3;
    outline-offset: 2px;
    background-color: #E3F2FD;
}

/* Buttons */
button:focus {
    outline: 3px solid #4CAF50;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.3);
}

/* Inputs */
input:focus,
textarea:focus,
select:focus {
    border-color: #2196F3;
    box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.2);
    outline: none;
}

/* Custom elements */
[tabindex]:focus {
    outline: 2px dashed #FF9800;
    outline-offset: 4px;
}
</style>`;

    const naturalTabOrderCode = `<!-- Tab order follows DOM order --&gt;
<input type="text">      <!-- Tab order: 1 --&gt;
<button>Submit</button>  <!-- Tab order: 2 --&gt;
<a href="/help">Help</a> <!-- Tab order: 3 --&gt;

<!-- This is best - predictable and logical --&gt;`;

    const customTabOrderCode = `<!--  Don't use positive tabindex values --&gt;
<input type="text" tabindex="3">
<button tabindex="1">Submit</button>
<a href="/help" tabindex="2">Help</a>

<!-- Confusing! Tab order is now: Button  Link  Input --&gt;
<!-- Very hard to maintain --&gt;`;

    const customInteractiveElementCode = `<div role="button" 
     tabindex="0" 
     onclick="handleClick()"
     onkeydown="handleKeyDown(event)">
    Custom Button
</div>

<script>
function handleKeyDown(event) {
    // Enter or Space activates buttons
    if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleClick();
    }
}

function handleClick() {
    console.log('Button activated!');
}
</script>

<!-- Must handle both click AND keyboard events --&gt;`;

    const commonKeyboardPatternsCode = `<script>
// Enter and Space for buttons
if (event.key === 'Enter' || event.key === ' ') {
    button.click();
}

// Escape to close dialogs
if (event.key === 'Escape') {
    closeDialog();
}

// Arrow keys for navigation
if (event.key === 'ArrowDown') {
    focusNextItem();
}
if (event.key === 'ArrowUp') {
    focusPreviousItem();
}

// Home/End for first/last
if (event.key === 'Home') {
    focusFirstItem();
}
if (event.key === 'End') {
    focusLastItem();
}
</script>`;

    const focusTrapCode = `<div role="dialog" aria-modal="true" id="myDialog">
    <h2>Dialog Title</h2>
    <button id="close-btn">Close</button>
    <input type="text">
    <button id="save-btn">Save</button>
</div>

<script>
const dialog = document.getElementById('myDialog');
const focusableElements = dialog.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
);
const firstElement = focusableElements[0];
const lastElement = focusableElements[focusableElements.length - 1];

// Trap focus
dialog.addEventListener('keydown', (e) => {
    if (e.key === 'Tab') {
        if (e.shiftKey) {
            // Shift+Tab
            if (document.activeElement === firstElement) {
                e.preventDefault();
                lastElement.focus();
            }
        } else {
            // Tab
            if (document.activeElement === lastElement) {
                e.preventDefault();
                firstElement.focus();
            }
        }
    }
    
    // Escape to close
    if (e.key === 'Escape') {
        closeDialog();
    }
});

// Focus first element when dialog opens
firstElement.focus();
</script>`;

    const skipLinkCode = `<body>
    <a href="#main-content" class="skip-link">
        Skip to main content
    </a>
    <a href="#nav" class="skip-link">
        Skip to navigation
    </a>
    
    <header>
        <nav id="nav">
            <!-- Many navigation links --&gt;
        </nav>
    </header>
    
    <main id="main-content">
        <!-- Main content --&gt;
    </main>
</body>

<style>
.skip-link {
    position: absolute;
    top: -40px;
    left: 0;
    padding: 8px;
    background: #000;
    color: white;
    text-decoration: none;
    z-index: 100;
}

.skip-link:focus {
    top: 0;
}
</style>`;

    const moveFocusCode = `<button onclick="openDialog()">Open Dialog</button>

<div id="dialog" hidden>
    <h2 id="dialog-title">Dialog</h2>
    <button id="dialog-close">Close</button>
</div>

<script>
let previousFocus;

function openDialog() {
    // Store current focus
    previousFocus = document.activeElement;
    
    // Show dialog
    dialog.hidden = false;
    
    // Move focus to dialog
    document.getElementById('dialog-close').focus();
}

function closeDialog() {
    // Hide dialog
    dialog.hidden = true;
    
    // Return focus to trigger element
    if (previousFocus) {
        previousFocus.focus();
    }
}
</script>`;

    const focusDynamicCode = `<button onclick="loadMore()">Load More</button>
<div id="content"></div>

<script>
async function loadMore() {
    const newContent = await fetchContent();
    
    // Add new content
    const newItem = document.createElement('article');
    newItem.innerHTML = newContent;
    newItem.tabIndex = -1; // Make focusable
    content.appendChild(newItem);
    
    // Move focus to new content
    newItem.focus();
}
</script>`;

    const keyboardShortcutsCode = `<script>
// Global keyboard shortcuts
document.addEventListener('keydown', (e) => {
    // Don't trigger if typing in input
    if (e.target.matches('input, textarea')) return;
    
    // Cmd/Ctrl + K: Search
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        openSearch();
    }
    
    // ? : Show keyboard shortcuts help
    if (e.key === '?') {
        showKeyboardHelp();
    }
    
    // / : Focus search
    if (e.key === '/') {
        e.preventDefault();
        document.getElementById('search').focus();
    }
});
</script>

<!-- Provide keyboard shortcuts guide --&gt;
<dialog id="keyboard-help">
    <h2>Keyboard Shortcuts</h2>
    <dl>
        <dt><kbd>Ctrl</kbd> + <kbd>K</kbd></dt>
        <dd>Open search</dd>
        
        <dt><kbd>?</kbd></dt>
        <dd>Show this help</dd>
        
        <dt><kbd>/</kbd></dt>
        <dd>Focus search box</dd>
    </dl>
</dialog>`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Accessibility (A11Y)</div>
                <h1>Keyboard Navigation & Focus Management</h1>
                <p className="hero-lead">
                    Make your website fully keyboard accessible. Learn focus management, tab order,
                    keyboard shortcuts, and focus trapping for accessible web applications.
                </p>
            </div>

            <div className="content-section">
                <h2>Why Keyboard Accessibility Matters</h2>
                <p>
                    Many users rely on keyboards: motor impairments, blind users with screen readers,
                    power users who prefer keyboard shortcuts, and users with broken/missing mouse.
                </p>

                <div className="keyboard-users">
                    <ul>
                        <li>Screen reader users navigate with keyboard</li>
                        <li>Motor impairment users may only use keyboard</li>
                        <li>Power users prefer keyboard efficiency</li>
                        <li>Temporary situations (broken mouse, remote access)</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Focusable Elements</h2>

                <h3>Naturally Focusable</h3>
                <p>These elements are focusable by default:</p>
                <div className="code-example">
                    <CodeBlock language="html" code={naturallyFocusableCode} />
                </div>

                <h3>Making Elements Focusable</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={makeFocusableCode} />
                </div>

                <h3>Removing from Tab Order</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={removeFromTabOrderCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Focus Indicators</h2>

                <h3>Always Show Focus</h3>
                <div className="code-example">
                    <CodeBlock language="css" code={alwaysShowFocusCode} />
                </div>

                <h3>Focus Styles for Different Elements</h3>
                <div className="code-example">
                    <CodeBlock language="css" code={focusStylesCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Tab Order</h2>

                <h3>Natural Tab Order (Best)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={naturalTabOrderCode} />
                </div>

                <h3>Custom Tab Order (Avoid)</h3>
                <div className="code-example error">
                    <CodeBlock language="html" code={customTabOrderCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Keyboard Event Handling</h2>

                <h3>Custom Interactive Elements</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={customInteractiveElementCode} />
                </div>

                <h3>Common Keyboard Patterns</h3>
                <div className="code-example">
                    <CodeBlock language="javascript" code={commonKeyboardPatternsCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Focus Trapping (Modals)</h2>

                <h3>Trap Focus in Modal Dialog</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={focusTrapCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Skip Links</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={skipLinkCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Managing Focus</h2>

                <h3>Moving Focus Programmatically</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={moveFocusCode} />
                </div>

                <h3>Focus After Dynamic Content</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={focusDynamicCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Keyboard Shortcuts</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={keyboardShortcutsCode} />
                </div>
            </div>

            <div className="content-section">
                <h2>Testing Keyboard Accessibility</h2>

                <h3>Manual Testing Checklist</h3>
                <ul>
                    <li> Unplug mouse and navigate entire site with keyboard</li>
                    <li> Press Tab - can you reach all interactive elements?</li>
                    <li> Press Shift+Tab - can you go backwards?</li>
                    <li> Press Enter/Space on buttons - do they activate?</li>
                    <li> Press Escape - do modals close?</li>
                    <li> Are focus indicators always visible?</li>
                    <li> Is tab order logical (left-to-right, top-to-bottom)?</li>
                    <li> Can you access dropdown menus?</li>
                    <li> Can you submit forms?</li>
                    <li> Can you close overlays/modals?</li>
                </ul>

                <h3>Common Keyboard Issues</h3>
                <ul>
                    <li> Removed focus outline (outline: none)</li>
                    <li> Div/span "buttons" without keyboard support</li>
                    <li> Custom dropdowns not keyboard accessible</li>
                    <li> Modals without focus trapping</li>
                    <li> Click-only interactions (no keyboard handler)</li>
                    <li> Content that appears on hover only</li>
                    <li> Skip links missing</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Best Practices</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use semantic HTML (button, a, input)</li>
                            <li>Always show focus indicators</li>
                            <li>Use tabindex="0" for custom controls</li>
                            <li>Handle both click and keyboard events</li>
                            <li>Trap focus in modals</li>
                            <li>Return focus after closing dialogs</li>
                            <li>Provide skip links</li>
                            <li>Test with keyboard only</li>
                            <li>Use logical tab order</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Remove focus outlines</li>
                            <li>Use positive tabindex values</li>
                            <li>Create keyboard traps (except modals)</li>
                            <li>Rely on mouse-only interactions</li>
                            <li>Use div/span for buttons</li>
                            <li>Forget to handle Escape key</li>
                            <li>Make custom controls without keyboard support</li>
                            <li>Skip keyboard testing</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default HTMLKeyboardNav;



